import { faker } from '@faker-js/faker';

import { coerceBoolean, formatString, isDefined, objectValues, random } from '@hsp/utilities';

import audienceDetails from 'assets/data/fake/audience-details.json';
import audienceDonations from 'assets/data/fake/audience-donations.json';
import audiencePage1 from 'assets/data/fake/audience-page-1.json';
import audiencePage2 from 'assets/data/fake/audience-page-2.json';
import { defaultMockTime } from 'constants/index';
import { Uri } from 'constants/uri';
import { IAbOrder } from 'types/api/ab-order';
import {
  AudienceParameters,
  IAudience,
  IAudienceAggregate,
  IAudienceAggregateMultiRfm,
  IAudienceDonations,
  IAudienceProvider,
  IAudienceSaveResult,
  IAudienceSearch,
  IAudienceSearchApi,
  IAudienceShop,
  ICompositeApi,
  ICompositeData,
  IMasterFile,
  IMultiRfmSearch,
  IOmitFlag,
  MultiRfmSearchApi,
} from 'types/api/audience';
import { IClient } from 'types/api/client';
import { IDataContainer, IResult, IResultPaged, IServerSidePagingResult } from 'types/api/common';
import { IDonor } from 'types/api/donor';
import { IHouseOrder } from 'types/api/house-order';
import { IListRental } from 'types/api/list-rental';
import { ITag } from 'types/api/tag';

import { BaseHttpService } from './http/base-http.service';

class AudienceService extends BaseHttpService {
  constructor() {
    super({ endpoints: objectValues(Uri.Audience) as string[] });
  }

  getAggregate(data: IAudienceSearchApi, cancel = false): Promise<IResult<IAudienceAggregate>> {
    const url = Uri.Audience.Aggregate;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  getAggregateMultiRfm(data: IMultiRfmSearch, cancel = false): Promise<IResult<IAudienceAggregateMultiRfm>> {
    const url = Uri.Audience.AggregateMultiRfm;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  getClientTags(cancel = false): Promise<IResult<ITag[]>> {
    const url = Uri.Audience.ClientTags;

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  getComposite(data: ICompositeApi, cancel = false): Promise<IResult<ICompositeData>> {
    const url = Uri.Audience.Composite;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  getDetails(id: string, dataSource?: string, cancel = false): Promise<IResult<IDataContainer<IAudienceProvider[]>>> {
    const url = Uri.Audience.Details;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { dataSource, id },
    });
  }

  getDetailsMultiRfm(
    id: string,
    data: IMultiRfmSearch,
    cancel = false
  ): Promise<IResult<IDataContainer<IAudienceProvider[]>>> {
    const url = Uri.Audience.DetailsMultiRfm;

    return this.post({ ...data, personId: id }, this.defaultRequestConfig(url, cancel));
  }

  getDonation(id: string, cancel = false): Promise<IResult<IDataContainer<IAudienceDonations[]>>> {
    const url = Uri.Audience.Donations;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { id },
    });
  }

  getMailTags(cancel = false): Promise<IResult<ITag[]>> {
    const url = Uri.Audience.MailTags;

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  getMasterFileAudience(audienceUid: string, cancel = false): Promise<IResult<IAudience>> {
    const url = `${Uri.Audience.MasterFile}/${audienceUid}`;

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  getMasterFileAudiences(
    parameters?: AudienceParameters,
    cancel = false
  ): Promise<IServerSidePagingResult<IAudience[]>> {
    const url = Uri.Audience.MasterFile;

    if (isDefined(parameters)) {
      const { category, filter, orderType, page, pageSize, showAll, sort, status, userId } = parameters;

      return this.get({
        ...this.defaultRequestConfig(url, cancel),
        params: { category, filter, orderType, page: page, pageSize, showAll, sort, status, userId },
      });
    } else {
      return this.get({
        ...this.defaultRequestConfig(url, cancel),
        params: { page: -1 },
      });
    }
  }

  getMasterFiles(cancel = false): Promise<IServerSidePagingResult<IMasterFile[]>> {
    const url = Uri.Audience.MasterFiles;

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  getOmitFlags(cancel = false): Promise<IResult<IDataContainer<IOmitFlag[]>>> {
    const url = Uri.Audience.Omits;

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  getPreBuiltAudience(audienceUid: string, cancel = false): Promise<IResult<IAudience>> {
    const url = `${Uri.Audience.PreBuilt}/${audienceUid}`;

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  getPreBuiltAudiences(parameters?: AudienceParameters, cancel = false): Promise<IServerSidePagingResult<IAudience[]>> {
    const url = Uri.Audience.PreBuilt;

    if (isDefined(parameters)) {
      const { category, filter, orderType, page, pageSize, showAll, sort, status, userId } = parameters;

      return this.get({
        ...this.defaultRequestConfig(url, cancel),
        params: { category, filter, orderType, page: page, pageSize, showAll, sort, status, userId },
      });
    } else {
      return this.get({
        ...this.defaultRequestConfig(url, cancel),
        params: { page: -1 },
      });
    }
  }

  getPreviousAbOrders(clients: string, cancel = false): Promise<IResult<IAbOrder[]>> | undefined {
    const url = Uri.Audience.PreviousAbOrders;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { clients },
    });
  }

  getPreviousHouseOrders(clients: string, cancel = false): Promise<IResult<IDataContainer<IHouseOrder[]>>> | undefined {
    const url = Uri.Audience.PreviousHouseOrder;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { clients },
    });
  }

  getPreviousListRentals(clients: string, cancel = false): Promise<IResult<IListRental[]>> | undefined {
    const url = Uri.Audience.PreviousListRentals;

    return this.get({
      ...this.defaultRequestConfig(url, cancel),
      params: { clients },
    });
  }

  getTags(cancel = false): Promise<IResult<ITag[]>> {
    const url = Uri.Audience.Tags;

    return this.get(this.defaultRequestConfig(url, cancel));
  }

  saveMasterFile(data: IAudienceShop, cancel = false): Promise<IResult<IAudienceSaveResult>> {
    const url = Uri.Audience.MasterFile;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  saveMasterFileMultiRfm(data: IAudienceShop, cancel = false): Promise<IResult<IAudienceSaveResult>> {
    const url = Uri.Audience.SaveMasterFileMultiRfm;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  savePreBuilt(data: IAudienceShop, cancel = false): Promise<IResult<IAudienceSaveResult>> {
    const url = Uri.Audience.PreBuilt;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  savePreBuiltMultiRfm(data: IAudienceShop, cancel = false): Promise<IResult<IAudienceSaveResult>> {
    const url = Uri.Audience.SavePreBuiltMultiRfm;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  searchResults(data: IAudienceSearchApi, cancel = false): Promise<IResultPaged<IDonor[]>> {
    const url = Uri.Audience.Results;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  searchResultsMultiRfm(data: MultiRfmSearchApi, cancel = false): Promise<IResultPaged<IDonor[]>> {
    const url = Uri.Audience.ResultsMultiRfm;

    return this.post(data, this.defaultRequestConfig(url, cancel));
  }

  updateMasterFile(audienceUid: string, data: IAudienceShop, cancel = false): Promise<IResult<IAudienceSaveResult>> {
    const url = formatString(Uri.Audience.UpdateMasterFile, audienceUid);

    return this.put(data, this.defaultRequestConfig(url, cancel));
  }

  updateMasterFileMultiRfm(
    audienceUid: string,
    data: IAudienceShop,
    cancel = false
  ): Promise<IResult<IAudienceSaveResult>> {
    const url = formatString(Uri.Audience.UpdateMasterFileMultiRfm, audienceUid);

    return this.put(data, this.defaultRequestConfig(url, cancel));
  }

  updatePreBuilt(audienceUid: string, data: IAudienceShop, cancel = false): Promise<IResult<IAudienceSaveResult>> {
    const url = formatString(Uri.Audience.UpdatePreBuilt, audienceUid);

    return this.put(data, this.defaultRequestConfig(url, cancel));
  }

  updatePreBuiltMultiRfm(
    audienceUid: string,
    data: IAudienceShop,
    cancel = false
  ): Promise<IResult<IAudienceSaveResult>> {
    const url = formatString(Uri.Audience.UpdatePreBuiltMultiRfm, audienceUid);

    return this.put(data, this.defaultRequestConfig(url, cancel));
  }
}

export const audienceService = new AudienceService();

if (coerceBoolean(process.env.REACT_APP_API_AUDIENCE_FAKE)) {
  const mocked = audienceService.mock(defaultMockTime);
  const mock = mocked?.adapter;

  mock?.onPost(Uri.Audience.Aggregate).reply((): Promise<[number, IResult<IAudienceAggregate>]> => {
    return new Promise((resolve) => {
      const result: IResult<IAudienceAggregate> = {
        dataContainer: {
          averageDonation: random(100000),
          newDonor: random(100000),
          repeatDonor: random(100000),
          totalCount: random(100000),
        },
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });

  mock?.onGet(Uri.Client.Clients).reply((): Promise<[number, IResult<IClient[]>]> => {
    return new Promise((resolve) => {
      const data: IClient[] = [];

      Array.from({ length: random(50) }).forEach(() => {
        data.push({
          acronym: faker.string.alpha({ length: { max: 5, min: 3 } }),
          id: faker.number.int().toString(),
          isInactive: false,
          name: faker.word.noun(),
        });
      });

      const result: IResult<IClient[]> = {
        dataContainer: data as unknown as IClient[],
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });

  mock?.onGet(Uri.Audience.ClientTags).reply((): Promise<[number, IResult<ITag[]>]> => {
    return new Promise((resolve) => {
      const data: ITag[] = [];

      Array.from({ length: random(50) }).forEach(() => {
        data.push({
          id: faker.number.int(),
          name: faker.word.noun(),
        });
      });

      const result: IResult<ITag[]> = {
        dataContainer: data as unknown as ITag[],
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });

  mock?.onGet(Uri.Audience.Details).reply((): Promise<[number, IResult<IAudienceProvider[]>]> => {
    return new Promise((resolve) => {
      resolve([200, audienceDetails as unknown as IResult<IAudienceProvider[]>]);
    });
  });

  mock?.onGet(Uri.Audience.Donations).reply((): Promise<[number, IResult<IAudienceDonations[]>]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve([200, audienceDonations as unknown as IResult<IAudienceDonations[]>]);
      }, random(defaultMockTime));
    });
  });

  mock?.onGet(Uri.Audience.MailTags).reply((): Promise<[number, IResult<ITag[]>]> => {
    return new Promise((resolve) => {
      const data: ITag[] = [];

      Array.from({ length: random(50) }).forEach(() => {
        data.push({
          id: faker.number.int(),
          name: faker.word.noun(),
        });
      });

      const result: IResult<ITag[]> = {
        dataContainer: data as unknown as ITag[],
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });

  mock?.onGet(Uri.Audience.Omits).reply((): Promise<[number, IResult<IOmitFlag[]>]> => {
    return new Promise((resolve) => {
      const data: IOmitFlag[] = [];

      Array.from({ length: random(50) }).forEach(() => {
        data.push({
          id: faker.number.int(),
          name: faker.word.noun(),
        });
      });

      const result: IResult<IOmitFlag[]> = {
        dataContainer: data as unknown as IOmitFlag[],
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });

  mock?.onPost(Uri.Audience.Results).reply(({ data }): Promise<[number, IResultPaged<IDonor[]>]> => {
    let { page } = JSON.parse(data) as IAudienceSearch;

    page = page ?? 1;

    return new Promise((resolve) => {
      const response = (page === 1 ? audiencePage1 : audiencePage2) as unknown as IResultPaged<IDonor[]>;

      resolve([200, response]);
    });
  });

  mock?.onGet(Uri.Audience.Tags).reply((): Promise<[number, IResult<ITag[]>]> => {
    return new Promise((resolve) => {
      const data: ITag[] = [];

      Array.from({ length: random(50) }).forEach(() => {
        data.push({
          id: faker.number.int(),
          name: faker.word.noun(),
        });
      });

      const result: IResult<ITag[]> = {
        dataContainer: data as unknown as ITag[],
        failure: false,
        statusCode: 200,
        success: true,
      };

      resolve([200, result]);
    });
  });
}
